// Hook for tenant-restricted location filtering
// Reads locationRestriction from tenant config to limit search to allowed states/cities
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppConfig } from "@/appConfig";

const useTenantLocations = () => {
  const { tenant } = useAppConfig();
  const allCities = useSelector((state) => state.locations.allCities);
  const restriction = tenant?.locationRestriction;
  const restrictedMode = restriction?.enabled === true;
  const allowedStates = restriction?.states || [];

  // Check if a city/state is within this tenant's allowed area
  const isLocationAllowed = useCallback(
    (city, state) => {
      if (!restrictedMode) return true;
      if (!state) return false;
      return allowedStates.some(
        (s) => s.toLowerCase() === state.toLowerCase().trim()
      );
    },
    [restrictedMode, allowedStates]
  );

  const searchCities = useCallback(
    (query) => {
      if (!restrictedMode || !query) return [];
      const q = query.toLowerCase().trim();
      const allowedSet = new Set(allowedStates.map((s) => s.toLowerCase()));
      return allCities
        .filter((c) => {
          const state = (c.StateCode || c.state || "").toLowerCase();
          if (!allowedSet.has(state)) return false;
          const city = (c.City || c.city || "").toLowerCase();
          const displayName = `${city}, ${state}`;
          return city.startsWith(q) || displayName.startsWith(q);
        })
        .map((c) => ({
          city: c.City || c.city,
          state: c.StateCode || c.state,
          displayName: `${c.City || c.city}, ${c.StateCode || c.state}`,
        }));
    },
    [restrictedMode, allowedStates, allCities]
  );

  // Default location for auto-setting on restricted tenants
  const defaultLocation = restrictedMode
    ? {
        city: restriction.defaultCity,
        state: restriction.defaultState,
        displayName: `${restriction.defaultCity}, ${restriction.defaultState}`,
      }
    : null;

  return {
    restrictedMode,
    allowedStates,
    defaultLocation,
    isLocationAllowed,
    searchCities,
  };
};

export default useTenantLocations;
